import React, {FC, useCallback} from 'react';
import {ButtonContentType, IProduct} from '../../../../types/galleryTypes';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {useAddToCartState} from './hooks/useAddToCartState';
import {useAddToCartText} from './hooks/useAddToCartText/useAddToCartText';
import {BaseAddToCartButton} from '../../BaseAddToCartButton/BaseAddToCartButton';
import {AddToCartFlowType, Experiments} from '../../../../constants';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import s from './AddToCartButton.scss';
import {useExperiments} from '@wix/yoshi-flow-editor';

export enum DataHook {
  AddToCartButton = 'product-item-add-to-cart-button',
}
export interface IAddToCart extends IGalleryGlobalProps {
  index: number;
  product: IProduct;
  quantity: number;
  showOnHoverClassName: string;
}

export const AddToCartComp: FC<IAddToCart> = ({index, product, quantity, showOnHoverClassName}) => {
  const {
    styles,
    stylesParams,
    handleAddToCart,
    textsMap: {selectOptionsButtonText},
  } = useGlobals();
  const {experiments} = useExperiments();
  const {id: productId} = product;
  const {isAddToCartEnabled, hasAddToCartUnselectedOptionError} = useAddToCartState(productId);
  const addToCartButtonText = useAddToCartText(productId, hasAddToCartUnselectedOptionError);

  const handleAddToCartButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      const isShowOnlyColorOptionsEnabled = styles.get(stylesParams.gallery_showColorOptionsOnly);
      const isProductOptionsEnabled = styles.get(stylesParams.gallery_showProductOptionsButton);
      const addToCartFlowType = styles.get(stylesParams.gallery_addToCartFlowType);
      const productOptionsDisplayOption = styles.get(stylesParams.gallery_productOptionsDisplayOption);
      const isAddToCartFlowTypeIsQuickAdd = addToCartFlowType === AddToCartFlowType.QUICK_ADD;

      void handleAddToCart({
        productId,
        index,
        quantity,
        isProductOptionsEnabled,
        isShowOnlyColorOptionsEnabled,
        isAddToCartFlowTypeIsQuickAdd,
        productOptionsDisplayOption,
      });
    },
    [productId, index, quantity, styles, stylesParams, handleAddToCart]
  );

  const commonBaseAddToCartButtonProps = {
    dataHook: DataHook.AddToCartButton,
    product,
    showOnHoverClassName,
    onClick: handleAddToCartButtonClick,
    disabled: !isAddToCartEnabled(),
    text: addToCartButtonText,
    hasError: hasAddToCartUnselectedOptionError,
    errorMessage: selectOptionsButtonText,
  };

  if (!experiments.enabled(Experiments.AllowAddToCartButtonContentTypesInViewer)) {
    return <BaseAddToCartButton {...commonBaseAddToCartButtonProps} contentType={ButtonContentType.Text} />;
  }

  return (
    <>
      <ConditionalRender by="showAddToCartTextButton" className={s.addToCartButtonConditionalContainer}>
        <BaseAddToCartButton {...commonBaseAddToCartButtonProps} contentType={ButtonContentType.Text} />
      </ConditionalRender>
      <ConditionalRender by="showAddToCartIconAndTextButton" className={s.addToCartButtonConditionalContainer}>
        <BaseAddToCartButton {...commonBaseAddToCartButtonProps} contentType={ButtonContentType.IconAndText} />
      </ConditionalRender>
      <ConditionalRender by="showAddToCartIconButton" className={s.addToCartButtonConditionalContainer}>
        <BaseAddToCartButton {...commonBaseAddToCartButtonProps} contentType={ButtonContentType.Icon} />
      </ConditionalRender>
    </>
  );
};

export const AddToCartButton = withGlobals(AddToCartComp);
